import { Typography } from '@mui/material';
import { GridColDef, GridRenderEditCellParams, GridRowModel } from '@mui/x-data-grid-pro';
import { get, isEmpty, set } from 'lodash';
import React, { Dispatch, SetStateAction } from 'react';
import { Person } from 'sr-types/lib/person/v1/graphql';
import { LinkedPeopleInfo } from 'sr-types/lib/production/v1/graphql';
import PersonListTile from '../../../common/components/person/PersonListTile';
import { Contact } from '../../../common/components/types';
import LookupGridField from '../../../common/list/LookupGridField';
import { failedToastMessage } from '../../../common/utils/commonUtils';
import { MARKETPLACE_ID } from '../../../marketplace/MarketplaceConstants';
import { PRODUCTION_ROLE_FIELDS, RESOURCE_TYPE } from '../../../production/productionConstants';

export const valueGetter = (value, row: GridRowModel) => {
    return isEmpty(row) ? undefined : value;
};

export const defaultOptionRenderer =
    (prop) =>
    ({ option }) => {
        const fontStyle = option.createOption ? 'italic' : 'normal';
        return (
            <Typography className="ellipsis" fontSize={'small'} fontStyle={fontStyle}>
                {get(option, prop)}
            </Typography>
        );
    };

export const defaultEntityToOption = (entity) => {
    return entity;
};

export const defaultFieldUpdater = (prop) => (colDefs, field, entity) => {
    const val = get(entity, prop);
    return [{ field: field, value: val }];
};

export const contactToOption = (contact) => {
    const names = contact.name.trim() ? contact.name.split(' ') : [];
    const primaryContact = contact.businessContacts?.find((contact: Contact) => contact.isPrimary);

    return {
        id: contact.id,
        name: {
            firstName: names[0],
            lastName: names.length > 1 ? names.slice(1).join(' ') : ''
        },
        contactInfo: primaryContact ? null : contact.contactInfo,
        personReference: contact.reference,
        organization: contact.organization,
        organizationReference: contact.organizationReference,
        role: contact.role,
        production: contact.production,
        productionReference: contact.productionReference,
        productionRole: contact.productionRole,
        organizationDepartment: contact.department,
        title: contact.title,
        productionDepartment: contact.productionDepartment
    };
};

export const organizationToOption = (organization) => {
    return {
        organization: organization.name,
        organizationReference: organization.reference
    };
};

export const productionToOption = (production) => {
    return {
        production: production.name,
        productionReference: production.reference
    };
};

export const contactToUpdate = (colDefs, field, entity) => {
    const update = [];
    colDefs.forEach((col) => {
        const val = get(entity, col.field);
        if (col.field === RESOURCE_TYPE && (val === undefined || val === null || val === '')) {
            return;
        }
        if (col.field === PRODUCTION_ROLE_FIELDS.roles || col.field === PRODUCTION_ROLE_FIELDS.productionRoles) {
            updateContactRoles(col, entity, val, update);
        } else {
            let cellValue = val;
            if (col.field === 'fullName') {
                cellValue = [
                    get(entity, 'name.firstName') ?? get(entity, 'candidateName.firstName'),
                    get(entity, 'name.lastName') ?? get(entity, 'candidateName.lastName')
                ]
                    .filter(Boolean)
                    .join(' ');
            }
            update.push({
                field: col.field,
                value: cellValue
            });
        }
    });
    return update;
};

export const organizationToUpdate = (colDefs, field, entity) => {
    return [
        { field: 'organization', value: get(entity, 'organization') },
        { field: 'organizationReference', value: get(entity, 'organizationReference') }
    ];
};

export const productionToUpdate = (colDefs, field, entity) => {
    return [
        { field: 'production', value: get(entity, 'production') },
        { field: 'productionReference', value: get(entity, 'productionReference') }
    ];
};

export const firstName: GridColDef = {
    field: 'name.firstName',
    headerName: 'First name',
    flex: 1,
    sortable: true,
    editable: true,
    minWidth: 120,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Person' }]}
                OptionRenderer={PersonListTile}
                toOption={contactToOption}
                entityToUpdate={contactToUpdate}
                filter={{ expressions: [{ field: 'public', value: { values: ['false'] } }] }}
            />
        );
    }
};

export const lastName: GridColDef = {
    field: 'name.lastName',
    headerName: 'Last name',
    flex: 1,
    sortable: true,
    editable: true,
    minWidth: 120,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Person' }]}
                OptionRenderer={PersonListTile}
                toOption={contactToOption}
                entityToUpdate={contactToUpdate}
                filter={{ expressions: [{ field: 'public', value: { values: ['false'] } }] }}
            />
        );
    }
};
export const organization: GridColDef = {
    field: 'organization',
    headerName: 'Organization',
    flex: 1,
    sortable: true,
    editable: true,
    type: 'string',
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Organization' }]}
                OptionRenderer={defaultOptionRenderer('organization')}
                toOption={organizationToOption}
                entityToUpdate={organizationToUpdate}
            />
        );
    }
};
export const email: GridColDef = {
    field: 'contactInfo.email.address',
    headerName: 'Email',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Person' }]}
                OptionRenderer={PersonListTile}
                toOption={contactToOption}
                entityToUpdate={contactToUpdate}
                filter={{ expressions: [{ field: 'public', value: { values: ['false'] } }] }}
            />
        );
    }
};

export const phone: GridColDef = {
    field: 'contactInfo.phone.number',
    headerName: 'Phone',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter
};

export const title: GridColDef = {
    field: 'title',
    headerName: 'Title',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Title' }]}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
            />
        );
    }
};
export const department: GridColDef = {
    field: 'organizationDepartment',
    headerName: 'Department',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Department' }]}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
            />
        );
    }
};
export const role: GridColDef = {
    field: 'role',
    headerName: 'Professional Role',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Role' }]}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
            />
        );
    }
};

export const production: GridColDef = {
    field: 'production',
    headerName: 'Production',
    flex: 1,
    sortable: true,
    editable: true,
    type: 'string',
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Production' }]}
                OptionRenderer={defaultOptionRenderer('production')}
                toOption={productionToOption}
                entityToUpdate={productionToUpdate}
            />
        );
    }
};
export const productionRole: GridColDef = {
    field: 'productionRole',
    headerName: 'Production Role',
    flex: 0.75,
    sortable: true,
    editable: true,
    type: 'string',
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Role' }]}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
            />
        );
    }
};

export const productionDepartment: GridColDef = {
    field: 'productionDepartment',
    headerName: 'Production Department',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Department' }]}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
            />
        );
    }
};

const fullName: GridColDef = {
    field: 'fullName',
    headerName: 'Name',
    flex: 1,
    sortable: true,
    editable: true,
    minWidth: 120,
    valueGetter: (_, row) => {
        if (row?.fullName) return row.fullName;
        const firstName = row?.['name.firstName'] ?? row?.name?.firstName ?? row?._raw?.name?.firstName ?? '';
        const lastName = row?.['name.lastName'] ?? row?.name?.lastName ?? row?._raw?.name?.lastName ?? '';
        return [firstName, lastName].filter(Boolean).join(' ');
    }
};

export const personReference: GridColDef = {
    field: 'personReference',
    headerName: 'Person Reference',
    flex: 1,
    sortable: true,
    editable: true,
    hideable: false,
    valueGetter: valueGetter
};

export const productionReference: GridColDef = {
    field: 'productionReference',
    headerName: 'Production Reference',
    flex: 1,
    sortable: true,
    editable: true,
    hideable: false,
    valueGetter: valueGetter
};

export const organizationReference: GridColDef = {
    field: 'organizationReference',
    headerName: 'Organization Reference',
    flex: 1,
    sortable: true,
    editable: true,
    hideable: false,
    valueGetter: valueGetter
};

export const roles: GridColDef = {
    field: 'roles',
    headerName: 'Professional Roles',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Role' }]}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
            />
        );
    }
};

export const productionRoles: GridColDef = {
    field: 'productionRoles',
    headerName: 'Production Roles',
    flex: 0.75,
    sortable: true,
    editable: true,
    type: 'string',
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'Role' }]}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
            />
        );
    }
};

export const resourceType: GridColDef = {
    field: 'resourceType',
    headerName: 'Type',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'ResourceType' }]}
                filter={{ expressions: [{ field: 'labels', value: { values: ['Individual'] } }] }}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
                allowFreeText={false}
            />
        );
    }
};

export const resourceSubType: GridColDef = {
    field: 'resourceSubType',
    headerName: 'Sub Type',
    flex: 0.75,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter,
    renderEditCell: (params: GridRenderEditCellParams) => {
        return (
            <LookupGridField
                {...params}
                columns={colDefs}
                filters={[{ identifier: 'entity', value: 'CatalogSubType' }]}
                OptionRenderer={defaultOptionRenderer('name')}
                toOption={defaultEntityToOption}
                entityToUpdate={defaultFieldUpdater('name')}
            />
        );
    }
};

export const resourceLabel: GridColDef = {
    field: 'resourceLabel',
    headerName: 'Resource Label',
    flex: 1,
    sortable: true,
    editable: true,
    minWidth: 80,
    valueGetter: valueGetter
};

const contactListColDefByField: Record<string, GridColDef> = {
    fullName,
    'name.firstName': firstName,
    'name.lastName': lastName,
    organization,
    'contactInfo.email.address': email,
    'contactInfo.phone.number': phone,
    title,
    organizationDepartment: department,
    role,
    roles,
    production,
    productionRole,
    productionRoles,
    productionDepartment,
    resourceLabel,
    resourceType,
    resourceSubType,
    personReference,
    productionReference,
    organizationReference
};

export const constructContactListColDefs = (fieldSequence: string[]): GridColDef[] => {
    return fieldSequence
        .map((field) => contactListColDefByField[field])
        .filter((colDef): colDef is GridColDef => !!colDef);
};

export const defaultContactListColumnSequence: string[] = [
    'name.firstName',
    'name.lastName',
    'organization',
    'contactInfo.email.address',
    'contactInfo.phone.number',
    'title',
    'organizationDepartment',
    'role',
    'production',
    'productionRole',
    'productionDepartment',
    'personReference',
    'productionReference',
    'organizationReference'
];

export const productionContactListColumnSequence: string[] = [
    'name.firstName',
    'name.lastName',
    'productionRoles',
    'productionDepartment',
    'organization',
    'roles',
    'organizationDepartment',
    'resourceLabel',
    'resourceType',
    'resourceSubType',
    'contactInfo.email.address',
    'contactInfo.phone.number',
    'title',
    'personReference',
    'productionReference',
    'organizationReference'
];

export const supplierGuestContactListColumnSequence: string[] = [...defaultContactListColumnSequence];
export const peoplePreviewContactListColumnSequence: string[] = [
    'fullName',
    'name.firstName',
    'name.lastName',
    ...productionContactListColumnSequence.filter((field) => field !== 'name.firstName' && field !== 'name.lastName')
];
export const peoplePreviewImportColumnSequence: string[] = [
    'fullName',
    ...productionContactListColumnSequence.filter((field) => field !== 'name.firstName' && field !== 'name.lastName')
];

export const colDefs: GridColDef[] = constructContactListColDefs(defaultContactListColumnSequence);
export const supplierGuestColDefs: GridColDef[] = constructContactListColDefs(supplierGuestContactListColumnSequence);

export const productionColDefs: GridColDef[] = constructContactListColDefs(productionContactListColumnSequence);

export const peoplePreviewProductionColDefs: GridColDef[] = constructContactListColDefs(
    peoplePreviewContactListColumnSequence
).map((col) => (col.field === 'name.firstName' || col.field === 'name.lastName' ? { ...col, hide: true } : col));

export const peoplePreviewAssociatedHiddenFields = [
    'name.firstName',
    'name.lastName',
    'title',
    'personReference',
    'productionReference',
    'organizationReference'
];

const peoplePreviewAssociatedHiddenFieldsSet = new Set(peoplePreviewAssociatedHiddenFields);

export const peoplePreviewAssociatedColDefs: GridColDef[] = peoplePreviewProductionColDefs.map((col) => {
    if (peoplePreviewAssociatedHiddenFieldsSet.has(col.field)) {
        return { ...col, hide: true, hideable: false };
    }
    return col;
});
export const peoplePreviewImportColDefs: GridColDef[] = constructContactListColDefs(peoplePreviewImportColumnSequence);

export const columnVisibility = {
    personReference: false,
    productionReference: false,
    organizationReference: false
};

export const altColDefs: GridColDef[] = [
    fullName,
    organization,
    email,
    phone,
    title,
    department,
    role,
    production,
    productionRole,
    productionDepartment
];

export const linkedPeopleToRows = (linkedPeople: LinkedPeopleInfo[], colDefinitions: GridColDef[]) => {
    if (!linkedPeople) return [];
    return linkedPeople.map((linkedPerson, index) => {
        let rowId: string | number;
        const contact = (linkedPerson.contact ?? {}) as Contact;
        if (linkedPerson.id !== undefined && linkedPerson.id !== null) {
            rowId = linkedPerson.id;
        } else if (contact.personReference?.id) {
            rowId = contact.personReference.id;
        } else {
            const firstName = contact.name?.firstName || '';
            const lastName = contact.name?.lastName || '';
            const email = contact.contactInfo?.email?.address || '';
            const dataHash = `${firstName}_${lastName}_${email}_${index}`.replace(/[^a-zA-Z0-9_]/g, '');
            rowId = `temp_${dataHash}`;
        }
        const row: GridRowModel = { id: rowId };
        colDefinitions.forEach((col) => {
            row[col.field] = get(contact, col.field) ?? get(linkedPerson, col.field);
        });
        row['_raw'] = contact;
        const usesFullName = colDefinitions.some((c) => c.field === 'fullName');
        if (usesFullName && (row.fullName == null || String(row.fullName).trim() === '')) {
            const fn = get(contact, 'name.firstName') ?? '';
            const ln = get(contact, 'name.lastName') ?? '';
            const combined = [fn, ln].filter(Boolean).join(' ');
            if (combined) {
                row.fullName = combined;
            }
        }
        return row;
    });
};

export const contactsToRows = (contacts: Contact[], colDefinitions: GridColDef[]) => {
    return contacts
        ? contacts.map((contact, index) => {
              let rowId: string | number;
              if (contact.id !== undefined && contact.id !== null) {
                  const idStr = String(contact.id);
                  const isNumeric = /^\d+$/.test(idStr);

                  switch (true) {
                      case !isNumeric && idStr.startsWith('temp_') && !!contact.personReference?.id:
                          rowId = contact.personReference.id;
                          break;
                      default:
                          rowId = contact.id;
                  }
              } else if (contact.personReference?.id) {
                  rowId = contact.personReference.id;
              } else {
                  const firstName = contact.name?.firstName || '';
                  const lastName = contact.name?.lastName || '';
                  const email = contact.contactInfo?.email?.address || '';
                  const dataHash = `${firstName}_${lastName}_${email}_${index}`.replace(/[^a-zA-Z0-9_]/g, '');
                  rowId = `temp_${dataHash}`;
              }
              const row: GridRowModel = { id: rowId };
              colDefinitions.forEach((col) => {
                  row[col.field] = get(contact, col.field);
              });
              row['_raw'] = contact;
              const usesFullName = colDefinitions.some((c) => c.field === 'fullName');
              if (usesFullName && (row.fullName == null || String(row.fullName).trim() === '')) {
                  const fn = get(contact, 'name.firstName') ?? '';
                  const ln = get(contact, 'name.lastName') ?? '';
                  const combined = [fn, ln].filter(Boolean).join(' ');
                  if (combined) {
                      row.fullName = combined;
                  }
              }
              return row;
          })
        : [];
};
export const rowsToContacts = (rows: GridRowModel[], colDefs: GridColDef[]): Contact[] => {
    return rows.map((row) => {
        return rowToContact(row, colDefs);
    });
};

export const rowToContact = (row: GridRowModel, colDefs: GridColDef[]): Contact => {
    const contact: Contact = {};
    const contactId = row?._raw?.id ?? row.id;
    set(contact, 'id', contactId);
    colDefs.forEach((col) => {
        // Small hack to deal with full name vs first/last name.
        if (col.field === 'fullName') {
            const raw = row.fullName;
            if (raw != null && String(raw).trim() !== '') {
                const fullName = String(raw).trim().replace(/\s+/g, ' ');
                const space = fullName.indexOf(' ');
                const firstName = space === -1 ? fullName : fullName.substring(0, space);
                const lastName = space === -1 ? '' : fullName.substring(space + 1).trim();
                set(contact, 'name.firstName', firstName);
                set(contact, 'name.lastName', lastName);
            } else {
                set(contact, 'name.firstName', row['name.firstName'] ?? row?._raw?.name?.firstName ?? '');
                set(contact, 'name.lastName', row['name.lastName'] ?? row?._raw?.name?.lastName ?? '');
            }
        } else {
            if (row[col.field] !== undefined) {
                if (row.fullName && (col.field === 'name.firstName' || col.field === 'name.lastName')) {
                    return;
                }
                set(contact, col.field, row[col.field]);
            }
        }
    });
    if (get(contact, 'contactInfo.email.address')) {
        set(contact, 'contactInfo.email.typeString', 'Business');
        set(contact, 'contactInfo.email.verified', false);
    } else {
        set(contact, 'contactInfo.email', null);
    }
    if (get(contact, 'contactInfo.phone.number')) {
        set(contact, 'contactInfo.phone.typeString', 'Mobile');
    } else {
        set(contact, 'contactInfo.phone', null);
    }
    // Normalize 'productionRoles' and set 'productionRole'
    let productionRoles = get(contact, 'productionRoles');
    if (!isEmpty(productionRoles)) {
        if (typeof productionRoles === 'string') {
            productionRoles = (productionRoles as string).split(',').map((role) => role.trim());
            set(contact, 'productionRoles', productionRoles);
        }
        set(contact, 'productionRole', productionRoles[0]);
    } else {
        delete contact.productionRoles;
    }

    // Normalize 'roles' and set 'role'
    let roles = get(contact, 'roles');
    if (!isEmpty(roles)) {
        if (typeof roles === 'string') {
            roles = (roles as string).split(',').map((role) => role.trim());
            set(contact, 'roles', roles);
        }
        set(contact, 'role', roles[0]);
    } else {
        delete contact.roles;
    }

    set(contact, 'personReference', row?.personReference || row?.reference || undefined);
    set(contact, 'productionReference', isEmpty(row.productionReference) ? undefined : row.productionReference);
    set(contact, 'organizationReference', isEmpty(row.organizationReference) ? undefined : row.organizationReference);
    return contact;
};

export const updateContactRoles = (col: any, entity: any, val: any, update: any[]) => {
    const fallback = col.field === PRODUCTION_ROLE_FIELDS.roles ? entity.role : entity.productionRole;
    let value = Array.isArray(val) ? val[0] : !isEmpty(val) ? val : fallback || undefined;
    if (!isEmpty(value)) {
        update.push({
            field: col.field,
            value
        });
    }
};

export function openMarketplaceAndFetchPerson(
    getPersonDetail: (id: string) => Promise<any>,
    setIsMarketplaceOpen?: Dispatch<SetStateAction<string | null>>,
    handleItemClick?: (item: Person | null) => void
) {
    return async (personReference?: { id?: string }) => {
        if (typeof setIsMarketplaceOpen === 'function') {
            setIsMarketplaceOpen(MARKETPLACE_ID);
        }

        if (handleItemClick && personReference?.id) {
            handleItemClick(null);
            try {
                const person = await getPersonDetail(personReference.id);
                handleItemClick(person);
            } catch (error) {
                failedToastMessage('person.fetch.error');
                setIsMarketplaceOpen?.(null);
            }
        }
    };
}
